import type { ProjectListItem } from "@/api/projects.api";
import { acceptHMRUpdate, defineStore } from "pinia";
import { computed, ref, watch } from "vue";
import { requestProjectSwitch } from "@/composables/useProjectSwitchGuard";
import { $t } from "@/services/i18n";
import { useUserProfile } from "@/stores/user-profile.store";

/**
 * Local storage key for persisting selected project
 */
const STORAGE_KEY = "sippulse:selected-project";

/**
 * Special value representing "All Projects" (global view)
 */
export const ALL_PROJECTS_ID = "__all__";

/**
 * AI-196: Display name for the Default workspace
 */
export const DEFAULT_WORKSPACE_NAME = "Default Workspace";

/**
 * Project Store
 *
 * Manages the currently selected project context for the application.
 * The selected project affects which resources are visible and where
 * new resources are created.
 *
 * Key behaviors:
 * - Selection persists in localStorage across sessions
 * - "All Projects" (global view) shows all resources with project badges
 * - When a specific project is selected, only that project's resources are shown
 * - Resources created while a project is selected get that project_id
 * - Resources created in "All Projects" view are org-level (project_id = null)
 *
 * @example
 * ```ts
 * const projectStore = useProjectStore();
 *
 * // Get selected project info
 * console.log(projectStore.selectedProjectId); // "proj-123" or "__all__"
 * console.log(projectStore.selectedProject); // ProjectListItem or null
 * console.log(projectStore.isGlobalView); // true if "All Projects" selected
 *
 * // Change selected project
 * projectStore.selectProject("proj-123");
 * projectStore.selectAllProjects(); // Switch to global view
 *
 * // Use for API calls
 * const headers = projectStore.getProjectHeader(); // { "X-Project-ID": "proj-123" } or {}
 * ```
 */
export const useProjectStore = defineStore("project", () => {
  // ==================== State ====================

  /**
   * User profile store (contains projects from /me response)
   */
  const userProfile = useUserProfile();

  /**
   * The currently selected project ID, or ALL_PROJECTS_ID for global view
   * Initialized with default - actual value loaded from user-specific localStorage
   */
  const selectedProjectId = ref<string>(ALL_PROJECTS_ID);

  // ==================== Computed ====================

  /**
   * All projects the user has access to (from /me endpoint).
   * The server returns appropriate projects based on user's role:
   * - Organization-scope users: all projects in org
   * - Project-scope roles: only projects user is member of
   */
  const projects = computed<ProjectListItem[]>(() => {
    return (userProfile.user?.projects ?? []) as ProjectListItem[];
  });

  /**
   * Whether projects are currently loading (waiting for /me response)
   */
  const isLoading = computed(() => userProfile.user === null);

  /**
   * Whether user can view "All Projects" (global view)
   * Based on role_scope from server - organization scope can see all
   */
  const canViewAllProjects = computed(() => {
    return userProfile.user?.role_scope === "organization";
  });

  /**
   * Storage key based on user_id for per-user persistence
   */
  const storageKey = computed(() => {
    const userId = userProfile.user?.user_id;
    const orgId = userProfile.user?.organization?.id;
    return userId && orgId ? `${STORAGE_KEY}:${userId}:${orgId}` : null;
  });

  /**
   * Whether we're in global view (All Projects)
   */
  const isGlobalView = computed(() => selectedProjectId.value === ALL_PROJECTS_ID);

  /**
   * The currently selected project, or null if in global view
   */
  const selectedProject = computed<ProjectListItem | null>(() => {
    if (isGlobalView.value) return null;
    return projects.value.find((p) => p.id === selectedProjectId.value) ?? null;
  });

  /**
   * The project ID to use for API calls (null for global view / org-level)
   */
  const currentProjectId = computed<string | null>(() => {
    if (isGlobalView.value) return null;
    return selectedProjectId.value;
  });

  /**
   * Display name for the currently selected project
   * AI-196 Phase 2: Returns "Default Workspace" for the Default workspace
   */
  const selectedProjectName = computed(() => {
    if (isGlobalView.value) return "All Projects";
    return selectedProject.value?.name ?? "Select Project";
  });

  // ==================== Actions ====================

  /**
   * Select a specific project
   * Shows confirmation dialog on protected routes, toast on success
   */
  async function selectProject(projectId: string) {
    // Skip if already selected
    if (projectId === selectedProjectId.value) return;

    const project = projects.value.find((p) => p.id === projectId);
    const projectName = project?.name ?? "";

    const confirmed = await requestProjectSwitch(projectId, projectName);
    if (!confirmed) return;

    selectedProjectId.value = projectId;
    saveSelectedProjectId(projectId);
  }

  /**
   * Switch to global view (All Projects)
   * Shows confirmation dialog on protected routes, toast on success
   */
  async function selectAllProjects() {
    // Skip if already in global view
    if (selectedProjectId.value === ALL_PROJECTS_ID) return;

    const confirmed = await requestProjectSwitch(ALL_PROJECTS_ID, $t("projects.all_projects"));
    if (!confirmed) return;

    selectedProjectId.value = ALL_PROJECTS_ID;
    saveSelectedProjectId(ALL_PROJECTS_ID);
  }

  /**
   * AI-196 Phase 2: Get the X-Project-ID header for API requests
   * Returns empty object for global view (no header needed)
   * All projects (including Default Workspace) now use real UUIDs
   */
  function getProjectHeader(): Record<string, string> {
    if (isGlobalView.value || !selectedProjectId.value) {
      return {};
    }
    return { "X-Project-ID": selectedProjectId.value };
  }

  /**
   * AI-196 Phase 2: Get the project_id value to use when creating new resources
   * Returns null for global view (org-level resource)
   * Default Workspace now has a real UUID, so it returns that UUID
   */
  function getProjectIdForCreate(): string | null {
    return currentProjectId.value;
  }

  /**
   * Validate that the selected project still exists
   * If not, switch to the first available project
   */
  function validateSelection() {
    if (!isGlobalView.value && !selectedProject.value && projects.value.length > 0) {
      // Selected project no longer exists, switch to first project
      const firstProject = projects.value[0];
      if (firstProject) {
        selectProject(firstProject.id);
      }
    }
  }

  // ==================== Persistence ====================

  /**
   * Save selected project ID to user-specific localStorage
   */
  function saveSelectedProjectId(projectId: string) {
    if (globalThis.window === undefined) return;
    const key = storageKey.value;
    if (!key) return;
    try {
      localStorage.setItem(key, projectId);
    } catch {
      // Ignore storage errors
    }
  }

  // ==================== Watchers ====================

  /**
   * Load and validate selection when user logs in
   * Uses user-specific localStorage key and validates permissions
   *
   * Fallback logic:
   * 1. If localStorage has a saved project AND user has access -> use saved
   * 2. If localStorage has ALL_PROJECTS_ID AND user is org-scope -> use ALL_PROJECTS_ID
   * 3. Otherwise -> select the first project in the user's list
   *
   * Note: We no longer default to ALL_PROJECTS_ID for new users.
   * The first project is always selected as default.
   */
  watch(
    [storageKey, () => userProfile.user?.projects],
    ([key, userProjects]) => {
      if (!key || !userProjects) return;

      const stored = localStorage.getItem(key);
      // Prefer first active (non-archived) project for fallback
      const firstActiveProject = userProjects.find((p) => (p as any).status !== "archived");
      const fallbackId = firstActiveProject?.id ?? userProjects[0]?.id ?? ALL_PROJECTS_ID;

      if (stored) {
        // Validate permission for stored project
        if (stored === ALL_PROJECTS_ID) {
          // Only use ALL_PROJECTS_ID if explicitly saved AND user has permission
          // This preserves the selection for users on the Organization Analytics page
          if (canViewAllProjects.value) {
            selectedProjectId.value = ALL_PROJECTS_ID;
          } else {
            // User doesn't have org-scope, select first active project
            selectedProjectId.value = fallbackId;
          }
        } else {
          // Check if user has access to the specific project
          const hasAccess = userProjects.some((p) => p.id === stored);
          selectedProjectId.value = hasAccess ? stored : fallbackId;
        }
      } else {
        // First time for this user - always default to first active project
        selectedProjectId.value = fallbackId;
      }

      // Save the validated selection
      saveSelectedProjectId(selectedProjectId.value);
    },
    { immediate: true }
  );

  // ==================== Return ====================

  return {
    // State
    selectedProjectId,
    projects,
    isLoading,

    // Computed
    isGlobalView,
    selectedProject,
    currentProjectId,
    selectedProjectName,
    canViewAllProjects,

    // Actions
    selectProject,
    selectAllProjects,
    getProjectHeader,
    getProjectIdForCreate,
    validateSelection,
  };
});

// Hot Module Replacement support
if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useProjectStore, import.meta.hot));
}

// ==================== Helper Functions ====================

/**
 * Composable for getting project context in components
 *
 * This is a convenience wrapper that provides common project-related
 * functionality without needing to import and set up the store manually.
 *
 * @example
 * ```vue
 * <script setup>
 * const { isGlobalView, selectedProject, selectProject } = useProjectContext();
 * </script>
 * ```
 */
export function useProjectContext() {
  const store = useProjectStore();

  return {
    // State
    projects: computed(() => store.projects),
    isLoading: computed(() => store.isLoading),
    isGlobalView: computed(() => store.isGlobalView),
    selectedProject: computed(() => store.selectedProject),
    selectedProjectId: computed(() => store.selectedProjectId),
    selectedProjectName: computed(() => store.selectedProjectName),
    currentProjectId: computed(() => store.currentProjectId),
    canViewAllProjects: computed(() => store.canViewAllProjects),

    // Actions
    selectProject: store.selectProject,
    selectAllProjects: store.selectAllProjects,
    getProjectHeader: store.getProjectHeader,
    getProjectIdForCreate: store.getProjectIdForCreate,
  };
}

/**
 * Returns the project name in uppercase for badge display.
 *
 * @param projectId - The project_id of the resource
 * @param projects - List of projects to look up the name
 * @returns Badge label: project name in uppercase, or "Unknown"
 */
export function getProjectBadgeLabel(
  projectId: string | null | undefined,
  projects: ProjectListItem[]
): string {
  if (!projectId) return "Unknown";
  const project = projects.find((p) => p.id === projectId);
  if (!project) return "Unknown";
  return project.name?.toUpperCase() ?? "Unknown";
}

/**
 * Get badge color for a project.
 */
export function getProjectBadgeColor(
  _projectId: string | null | undefined,
  _projects: ProjectListItem[]
): "blue" {
  return "blue";
}
